import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { QueryConfig } from '../api/client';
import {
  type InvoiceSortBy,
  type ListParams,
  type Paginated,
} from '@relay-towing/api';
import { listPath } from '../api/list';
import { api } from '../lib/api';
import { useDriverProfile } from './useDriverProfile';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';

export interface Invoice {
  id: string;
  invoiceNumber: string;
  dispatchId: string | null;
  clientName: string;
  total: number;
  status: 'DRAFT' | 'SENT' | 'PAID';
  squarePaymentStatus?: string | null;
  createdAt: string;
  issuedOn: string;
  dispatch?: {
    id: string;
    dispatchNumber: string;
    vehicle?: {
      vehicleYear?: number | null;
      vehicleMake?: string | null;
      /** Older rows keep the make here. */
      vehicleName?: string | null;
      vehicleModel?: string | null;
      licensePlate?: string | null;
    };
  };
}

interface InvoicesParams extends ListParams<InvoiceSortBy> {
  /**
   * Only invoices on calls assigned to the caller: a driver's own list. The
   * backend decides what the caller may see; this picks what the screen shows.
   */
  assignedToMe?: boolean;
  searchQuery?: string;
  status?: 'DRAFT' | 'SENT' | 'PAID';
}

const getInvoices = async (
  token: string,
  companyId: string,
  params?: InvoicesParams
): Promise<Paginated<Invoice>> => {
  console.log('📄 [useInvoices] Fetching invoices with params:', params);

  // Falsy values are left out, as the hand-built query string always did.
  // Through lib/api, not getList: its interceptor sends a signed-out user
  // back to /login.
  const response = await api.get(
    listPath('/invoices', {
      assignedToMe: params?.assignedToMe || undefined,
      sortBy: params?.sortBy || undefined,
      pageNo: params?.pageNo || undefined,
      pageSize: params?.pageSize || undefined,
      searchQuery: params?.searchQuery || undefined,
      status: params?.status || undefined,
    }),
    {
      headers: { Authorization: `Bearer ${token}`, 'x-company-id': companyId },
    }
  );
  const page = response.data as Paginated<Invoice>;

  console.log('📄 [useInvoices] Received invoices:', {
    total: page.data.length,
    withDispatchId: page.data.filter((inv) => inv.dispatchId).length,
    meta: page.meta,
  });

  return page;
};

/**
 * The invoice list: one endpoint for every role. The driver app's own list
 * (`assignedToMe`) used to be a separate endpoint that had drifted from this
 * one, returning deleted invoices and never paging.
 */
export const useInvoices = (
  params?: InvoicesParams,
  config?: QueryConfig<typeof getInvoices>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { data: driverProfile } = useDriverProfile();
  // An owner or admin with no Driver row gets a placeholder profile whose
  // companyId is '', so the stored selection stands in for it.
  const companyId = companyIdFromProfileOrStorage(driverProfile);

  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      if (!companyId) {
        throw new Error('Company ID is not available');
      }

      return getInvoices(token, companyId, params);
    },
    queryKey: ['invoices', params, companyId],
    enabled: isAuthenticated && !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
